import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// Interfaces
import { ILocationState } from "@/Common/interfaces/location";
import { IUserState } from "@/Common/interfaces/user";
import { ISalesPerformanceReportVersion } from "@/Settings/types/SalesPerformanceReportFormats/model";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API's
import { getSalesPerformanceReportVersions } from "@/Common/api/salesPerformance/queries";
import {
	deleteSalesPerformanceReportVersion,
	updateSalesPerformanceReportVersion,
} from "@/Settings/api/SalesPerformanceReportFormats/mutations";

// Components
import SalesPerformanceReportFormatsModel from "./SalesPerformanceReportFormatsModel";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import RemoveCircleRoundedIcon from "@mui/icons-material/RemoveCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";

const QUERY_IDENTIFIERS = ["salesPerformanceReportVersions", "salesPerformanceReportVersion"];

export default function SalesPerformanceReportFormatsLayer() {
	const { clearCache } = useCacheCleaner();
	const [selected, setSelected] = useState<ISalesPerformanceReportVersion | null>(null);
	const [title, setTitle] = useState<string | null>(null);
	const [disabledAdd, setDisabledAdd] = useState<boolean>(false);
	const VERSION_PADDING = 6;

	const user = useSelector((state: IUserState) => state.user.user);
	const location = useSelector((state: ILocationState) => state.location.location);
	const queryClient = useQueryClient();

	const { data: tree, isLoading: loadingTree } = useQuery({
		queryKey: ["salesPerformanceReportVersions", location?.locationId],
		queryFn: async () => {
			const reportVersions = await getSalesPerformanceReportVersions(location.locationId);
			if (reportVersions) {
				try {
					const modelVersion = reportVersions.at(-1);
					setSelected(modelVersion || null);
				} catch (error) {
					errorLogger(error instanceof Error ? error : new Error(String(error)));
				}
			}

			return reportVersions;
		},
		enabled: !!location?.locationId,
		staleTime: Infinity,
	});

	const { mutate: updateSalesPerformanceReport, isPending: updating } = useMutation({
		mutationFn: (input: ISalesPerformanceReportVersion) => updateSalesPerformanceReportVersion(input),
		onSuccess: (result) => {
			queryClient.setQueryData(
				["salesPerformanceReportVersions", location.locationId],
				(data: ISalesPerformanceReportVersion[] | undefined) => {
					let found = false;
					const updatedData = (data ?? []).map((element: ISalesPerformanceReportVersion) => {
						if (element._id === result._id) {
							found = true;
							return result;
						}
						return element;
					});

					if (!found) {
						updatedData.forEach((element: ISalesPerformanceReportVersion) => {
							if (element._id === "new") {
								element._id = result._id;
								element.title = result.title;
								found = true;
							}
						});

						setDisabledAdd(!found);
					}

					return updatedData;
				}
			);

			queryClient.setQueryData(["salesPerformanceReportVersion", location.locationId, result._id], () => {
				return result;
			});
		},
	});

	const { mutate: deleteSalesPerformanceReport, isPending: deleting } = useMutation({
		mutationFn: (input: { locationId: string; id: string }) => deleteSalesPerformanceReportVersion(input.locationId, input.id),
		onSuccess: (result) => {
			if (result) {
				queryClient.setQueryData(
					["salesPerformanceReportVersions", location.locationId],
					(data: ISalesPerformanceReportVersion[] | undefined) => {
						let select: ISalesPerformanceReportVersion | null = null;
						const updatedData = (data ?? []).filter((element: ISalesPerformanceReportVersion, key: number) => {
							if (element._id === selected?._id) {
								if (key) {
									select = data?.at(key - 1) || null;
								} else {
									select = data?.at(key + 1) || null;
								}
							}
							return element._id !== selected?._id;
						});
						setSelected(select);
						return updatedData;
					}
				);

				queryClient.removeQueries({ queryKey: ["salesPerformanceReportVersion", location.locationId, selected?._id] });
			}
		},
	});

	const handleSelection = (selection: ISalesPerformanceReportVersion) => {
		if (selected?._id !== selection?._id) {
			setSelected(selection);
		}
	};

	const getNextVersionName = (data: ISalesPerformanceReportVersion[]) => {
		if (data.length === 0) {
			const initialTitle = `${location.prefix}000000`;
			setTitle(initialTitle);
			return initialTitle;
		}
		const lastVersionTitle = data[data.length - 1].title;
		const numericPart = lastVersionTitle.replace(location.prefix, "");
		const versionNumber = isNaN(parseInt(numericPart, 10)) ? 1 : parseInt(numericPart, 10) + 1;
		const titleName = `${location.prefix}${versionNumber.toString().padStart(VERSION_PADDING, "0")}`;
		setTitle(titleName);
		return titleName;
	};

	const addVersion = () => {
		const newVersion: ISalesPerformanceReportVersion = {
			_id: "new",
			title: getNextVersionName(tree ?? []),
			locked: false,
			locationId: "",
			effectiveDateTime: null,
			saleDate: "",
			dealNumber: "",
			customerName: "",
			customerNumber: "",
			stockNumber: "",
			description: "",
			saleType: "",
			dealType: "",
			units: "",
			saleAmount: "",
			salesEmployeeId: "",
			financeEmployeeId: "",
			frontProducts: [],
			backProducts: [],
			createdOn: new Date(),
			createdBy: "",
			lastModifiedOn: new Date(),
			lastModifiedBy: "",
		};

		queryClient.setQueryData(
			["salesPerformanceReportVersions", location.locationId],
			(data: ISalesPerformanceReportVersion[] | undefined) => {
				return (data ?? []).concat(newVersion);
			}
		);

		setSelected(newVersion);
		setDisabledAdd(true);
	};

	const deleteVersion = () => {
		if (selected?._id === "new") {
			let select: ISalesPerformanceReportVersion | null = null;
			queryClient.setQueryData(
				["salesPerformanceReportVersions", location.locationId],
				(data: ISalesPerformanceReportVersion[] | undefined) => {
					const updatedData = (data ?? []).filter((element: ISalesPerformanceReportVersion) => element._id !== selected._id);
					select = updatedData.at(-1) || null;
					return updatedData;
				}
			);
			queryClient.removeQueries({ queryKey: ["salesPerformanceReportVersion", location.locationId, selected._id] });

			setDisabledAdd(false);
			setSelected(select);
		} else if (selected?._id) {
			const input = {
				locationId: location.locationId,
				id: selected._id,
			};

			deleteSalesPerformanceReport(input);
		}
	};

	const updateVersion = (modelVersion: ISalesPerformanceReportVersion) => {
		queryClient.setQueryData(
			["salesPerformanceReportVersions", location.locationId],
			(data: ISalesPerformanceReportVersion[] | undefined) => {
				return (data ?? []).map((element: ISalesPerformanceReportVersion) => {
					if (element._id === modelVersion._id) {
						element.title = modelVersion.title;
					}
					return element;
				});
			}
		);

		if (modelVersion._id === "new") {
			delete modelVersion["_id"];
		}

		if (modelVersion?.datePeriods) {
			delete modelVersion["datePeriods"];
		}

		updateSalesPerformanceReport(modelVersion);
	};

	useEffect(() => {
		if (!user || !location) {
			return () => {};
		}

		return () => {
			setDisabledAdd(false);
			setSelected(null);
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [user, location]);

	const isPerformingAction = updating || deleting;
	const isDisabledOptions = loadingTree || isPerformingAction;

	return (
		<BodyLayer
			fixedContent={
				<AppSideBar
					searchProps={{
						loading: loadingTree,
					}}
					sideBarProps={{
						tree: tree || [],
						selected: selected?._id ?? "",
						loading: loadingTree,
						disabled: loadingTree,
						onSelectAction: handleSelection,
					}}
					actions={{
						spacing: 3,
						operations: [
							{
								identifier: "delete",
								classes: "btn-delete",
								color: "var(--gray-blue-color)",
								icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: "35px" }} />,
								iconHover: <RemoveCircleRoundedIcon sx={{ fontSize: "35px" }} />,
								disabled: isDisabledOptions || selected?.locked || !selected,
								doubleCheckingAction: true,
								loading: loadingTree,
								onClick: deleteVersion,
							},
							{
								identifier: "add",
								classes: "btn-add",
								color: "var(--primary-color)",
								icon: <AddCircleOutlineRoundedIcon sx={{ fontSize: "35px" }} />,
								iconHover: <AddCircleRounded sx={{ fontSize: "35px" }} />,
								disabled: isDisabledOptions || disabledAdd,
								loading: loadingTree,
								onClick: addVersion,
							},
						],
					}}
				/>
			}
		>
			<RoundedBox sx={{ minHeight: "630px" }}>
				<SalesPerformanceReportFormatsModel
					title={title ?? ""}
					model={selected}
					location={location}
					user={user?._id ? { _id: user._id } : null}
					onActionUpdate={updateVersion}
					loading={loadingTree}
					disabled={updating}
				/>
			</RoundedBox>
		</BodyLayer>
	);
}
